import { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../constants/firebaseModular';
import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  // OAuthProvider,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  // signInWithRedirect,
  signOut,
  //eslint-disable-next-line
  User,
  //eslint-disable-next-line
  UserCredential,
} from 'firebase/auth';
// import * as Sentry from '@sentry/browser';

export interface IAuthContext {
  currentUser: User | null | undefined;
  firebaseGetUser: () => User | null | undefined;
  //eslint-disable-next-line
  firebaseLogin: (email: string, password: string) => Promise<UserCredential>;
  //eslint-disable-next-line
  // firebaseSignInWithRedirect: () => Promise<UserCredential | void>;
  /**
   * Exchange a server-issued custom token for a Firebase session (CU-86ajzr94n).
   *
   * ⚠️ ADDITIVE ONLY. This member was added for passkey login and changes
   * NOTHING about the password path, which stays permanently visible and
   * ungated. That is the whole shape of the change: the password rail cannot
   * regress here by construction, because no existing member was touched.
   *
   * ⚠️ NEVER DECODE THIS TOKEN TO MAKE A SECURITY DECISION. The rule ports from
   * `mideo-health-app`: a uid read out of the custom token is DISPLAY-GATING
   * ONLY. What we persist is Firebase's own verified uid, which arrives through
   * `onAuthStateChanged` below — the same path the password sign-in uses. A
   * tampered token then yields at worst a wrong prompt and still fails this
   * exchange, which is safe ONLY as long as nothing security-relevant keyed off
   * a client-side decode. The portal does not decode it at all, and the
   * `$patientId` UID-mismatch guard is the web answer to wrong-account routing.
   */
  firebaseSignInWithCustomToken: (_token: string) => Promise<UserCredential>;
  firebaseSignOut: () => Promise<void>;
  //eslint-disable-next-line
  firebaseSignUp: (email: string, password: string) => Promise<UserCredential>;
}

const AuthContext = createContext<IAuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>();
  const [loading, setLoading] = useState<boolean>(true);

  function firebaseLogin(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Passkey login (CU-86ajzr94n). The token is minted server-side only after the
  // WebAuthn assertion verified, so this call is the LAST link in that rail —
  // never the thing that decides whether the patient is who they say they are.
  function firebaseSignInWithCustomToken(token: string) {
    return signInWithCustomToken(auth, token);
  }

  function firebaseSignOut() {
    return signOut(auth);
  }

  function firebaseSignUp(email: string, password: string) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function firebaseGetUser() {
    return auth.currentUser;
  }

  // SET CURRENT USER AFTER VALID FIREBASE AUTH LOGIN
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      // HANDLE USERS AUTHENTICATING VIA PASSAGE REDIRECT
      const result = await getRedirectResult(auth);
      if (result) {
        user = result.user;
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    firebaseGetUser,
    firebaseLogin,
    firebaseSignInWithCustomToken,
    // firebaseSignInWithRedirect,
    firebaseSignOut,
    firebaseSignUp,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
